import { Menu, MenuItem } from "@mui/material";
import { Button } from "pepsico-ds";
import { useCallback, useState } from "react";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import ProductGroupModal from "./ProductGroupModal";
import ProductsTable from "./ProductsTable";

const ProductsListView = ({
  products,
  loading,
  filterData,
  productGroups,
  productGroupMap,
  handlers,
  onAddSingleProduct,
  onImportFile,
  onViewProduct,
  onEditProduct,
  onDeleteProduct,
  onDeleteMultiple,
  onSaveGroup,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const handleAddProductClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddSingleProduct = () => {
    handleMenuClose();
    onAddSingleProduct();
  };

  const handleImportFile = () => {
    handleMenuClose();
    onImportFile();
  };

  const handleSelectionChange = useCallback((ids) => {
    setSelectedProductIds(ids);
  }, []);

  const handleBulkAddToGroup = () => {
    setShowGroupModal(true);
  };

  const handleSaveGroupFromBulk = (groupName, productIds) => {
    onSaveGroup(groupName, productIds);
    setSelectedProductIds([]);
  };

  const handleBulkDelete = () => {
    setDeleteConfirmation({ type: "bulk", ids: selectedProductIds });
  };

  const handleRequestDeleteProduct = useCallback((id, name) => {
    setDeleteConfirmation({ type: "single", id, name });
  }, []);

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmation) return;
    if (deleteConfirmation.type === "bulk") {
      if (onDeleteMultiple) {
        onDeleteMultiple(deleteConfirmation.ids);
      }
      setSelectedProductIds([]);
    } else if (onDeleteProduct) {
      onDeleteProduct(deleteConfirmation.id, deleteConfirmation.name);
    }
    setDeleteConfirmation(null);
  };

  const selectedCount = selectedProductIds.length;

  const deleteConfirmationTitle =
    deleteConfirmation?.type === "bulk"
      ? `Delete ${deleteConfirmation.ids.length} selected product${
          deleteConfirmation.ids.length > 1 ? "s" : ""
        }?`
      : `Delete "${deleteConfirmation?.name}"?`;

  return (
    <div className="products-list-view">
      <div className="products-list-actions">
        <Button
          text="Add product"
          onClick={handleAddProductClick}
          size="medium"
          iconTrailing="arrow_drop_down"
          className="add-product-dropdown-btn"
        />
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleAddSingleProduct} sx={{ minWidth: "180px" }}>
            Add single product
          </MenuItem>
          <MenuItem onClick={handleImportFile} sx={{ minWidth: "180px" }}>
            Import file
          </MenuItem>
        </Menu>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-selection-count">
            {selectedCount} of {products.length} rows selected
          </span>
          <div className="bulk-action-buttons">
            <Button
              text="Delete"
              variant="secondary"
              size="small"
              onClick={handleBulkDelete}
            />
            <Button
              text="Add to product group"
              variant="primary"
              size="small"
              onClick={handleBulkAddToGroup}
            />
          </div>
        </div>
      )}

      <ProductsTable
        products={products}
        loading={loading}
        filterData={filterData}
        productGroups={productGroups}
        productGroupMap={productGroupMap}
        handleView={onViewProduct}
        handleEdit={onEditProduct}
        handleSoftDelete={handleRequestDeleteProduct}
        handleFilterChange={handlers.handleFilterChange}
        selectable={true}
        selectedProductIds={selectedProductIds}
        onSelectionChange={handleSelectionChange}
      />

      <ProductGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onSave={handleSaveGroupFromBulk}
        preSelectedProductIds={selectedProductIds}
      />

      <DeleteConfirmationModal
        isOpen={Boolean(deleteConfirmation)}
        title={deleteConfirmationTitle}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ProductsListView;
